import { Node, Extension, Mark, mergeAttributes } from "@tiptap/core";
import type { Editor } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { DOMParser as ProseMirrorDOMParser, Fragment } from '@tiptap/pm/model';
import { GapCursor } from '@tiptap/pm/gapcursor';
import { ImageView } from "./image-view";
import { imageDimension, imageElement, svgImageSource } from './pasted-images';
import { InlineImageView } from './inline-image-view';
import { VideoView } from "./video-view";
import { videoWidth, videoFrameStyle, videoBlockStyle, VideoWidth } from '../../general/youtube-video';
import { AudioView } from "./audio-view";

function deletePreviousPanel(editor: Editor, panelName: string): boolean {
  const { selection, doc } = editor.state;
  if (!editor.isEditable || !selection.empty) return false;

  let boundary: number;
  if (selection instanceof GapCursor) {
    boundary = selection.from;
  } else if (selection instanceof TextSelection) {
    const { $from } = selection;
    if (!$from.parent.isTextblock || $from.parentOffset !== 0 || $from.depth === 0) return false;
    boundary = $from.before();
  } else {
    return false;
  }

  // Only delete an immediately preceding sibling panel. Inside its text,
  // leave Backspace to the usual text editing commands.
  const previous = doc.resolve(boundary).nodeBefore;
  if (previous?.type.name !== panelName) return false;

  return editor.commands.deleteRange({
    from: boundary - previous.nodeSize,
    to: boundary,
  });
}

export const TextStyle = Mark.create({
  name: "textStyle",

  addAttributes() {
    return {
      fontFamily: { default: null },
      fontSize: { default: null },
      class: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attributes => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "span" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },
});

export const FontFamily = Extension.create({
  name: "fontFamily",

  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => element.style.fontFamily || null,
            renderHTML: (attributes) => {
              if (!attributes.fontFamily) return {};
              return { style: `font-family: ${attributes.fontFamily}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontFamily:
        (token: string | null) =>
        ({ editor }) => {
          const map: Record<string, string> = {
            arial: "Arial, Helvetica, sans-serif",
            helvetica: "Helvetica, Arial, sans-serif",
            inter: '"Inter", system-ui, sans-serif',
            roboto: '"Roboto", system-ui, sans-serif',
            georgia: "Georgia, serif",
            times: '"Times New Roman", Times, serif',
            mono: 'Menlo, Consolas, "Courier New", monospace',
          };

          const stack = token ? map[token] ?? token : null;
          editor.commands.updateAttributes("textStyle", { fontFamily: stack });
          return true;
        },

      unsetFontFamily:
        () =>
        ({ editor }) => {
          editor.commands.updateAttributes("textStyle", { fontFamily: null });
          return true;
        },
    };
  },
});

export const FontSize = Extension.create({
  name: "fontSize",

  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (size: string | null) =>
        ({ editor }) => {
          editor.commands.updateAttributes("textStyle", { fontSize: size });
          return true;
        },

      unsetFontSize:
        () =>
        ({ editor }) => {
          editor.commands.updateAttributes("textStyle", { fontSize: null });
          return true;
        },
    };
  },
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    bgColor: {
      setBgColor: (color: string) => ReturnType;
      unsetBgColor: () => ReturnType;
    };
  }
}

export const BackgroundColor = Mark.create({
  name: "bgColor",

  excludes: "",

  addAttributes() {
    return {
      backgroundColor: {
        default: null,
        parseHTML: element => element.style.backgroundColor || null,
        renderHTML: attributes => {
          const value = attributes.backgroundColor;
          if (!value) return {};

          const normalized = value.startsWith('#') ? value : `#${value}`;
          return { style: `background-color: ${normalized}` };
        },
      },
    };
  },

  parseHTML() {
    return [{ style: "background-color" }];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = mergeAttributes(HTMLAttributes);
    return ["span", attrs, 0];
  },

  addCommands() {
    return {
      setBgColor:
        (color: string) =>
        ({ chain }) => {
          const normalized = color.startsWith('#') ? color : `#${color}`;
          return chain()
            .setMark(this.name, { backgroundColor: normalized })
            .run();
        },

      unsetBgColor:
        () =>
        ({ chain }) => {
          return chain()
            .unsetMark(this.name)
            .run();
        },
    };
  },
});

export const ClassMark = Mark.create({
  name: "classMark",

  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: el => el.getAttribute("class"),
        renderHTML: attrs => attrs.class ? { class: attrs.class } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[class]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
});

export const BlockClass = Extension.create({
  name: "blockClass",

  addGlobalAttributes() {
    return [
      {
        types: [
          "paragraph",
          "heading",
          "blockquote",
          "listItem",
          "bulletList",
          "orderedList",
          "codeBlock",
          "horizontalRule",
          "taskItem",
          "taskList",
          "table",
          "tableRow",
          "tableCell",
        ],
        attributes: {
          class: {
            default: null,
            parseHTML: el => el.getAttribute("class"),
            renderHTML: attrs => attrs.class ? { class: attrs.class } : {},
          },
        },
      },
    ];
  },
});

export const FontSizeMark = Mark.create({
  name: 'fontSize',

  addAttributes() {
    return {
      class: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'span[class]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },
});

export const Collapsible = Node.create({
  name: "collapsible",
  group: "block",
  content: "collapsibleHeader collapsibleBody",
  defining: true,

  addKeyboardShortcuts() {
    return { Backspace: () => deletePreviousPanel(this.editor, this.name) };
  },

  addAttributes() {
    return {
      styleName: { default: "default" },
      backgroundColor: { default: null },
      borderColor: { default: null },
      fontColor: { default: null },
      borderWidth: { default: 1 },
      borderStyle: { default: "solid" },
      borderRadius: { default: 6 },
      padding: { default: 12 },
      shadow: { default: "none" },
      align: { default: "left" },
      textAlign: { default: "left" },
      overflow: { default: "visible" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div",
        getAttrs: (el) => {
          if (!el.classList.contains("collapsible")) return false;

          const style = el.getAttribute("style") ?? "";

          const get = (prop: string) => {
            const m = style.match(new RegExp(`${prop}:\\s*([^;]+)`));
            return m ? m[1].trim() : null;
          };

          function getStyleValue(style: string, prop: string): string | null {
            return style
                .split(";")
                .map(s => s.trim())
                .filter(Boolean)
                .reduce((acc, rule) => {
                const [key, value] = rule.split(":").map(x => x.trim());
                return key === prop ? value : acc;
                }, null as string | null);
            }

          const classes = Array.from((el as HTMLElement).classList);
          const panelClass = classes.find(c => c.startsWith("panel-"));
          const styleName = panelClass ? panelClass.replace("panel-", "") : "default";

          return {
            styleName,
            backgroundColor: getStyleValue(style, "background-color"),
            borderColor: getStyleValue(style, "border-color"),
            fontColor: getStyleValue(style, "color"),
            borderWidth: parseInt(get("border-width") || "1"),
            borderStyle: get("border-style"),
            borderRadius: parseInt(get("border-radius") || "6"),
            padding: parseInt(get("padding") || "12"),
            shadow: get("box-shadow"),
            textAlign: get("text-align"),
            overflow: get("overflow"),
            align: el.style.marginLeft === "auto" && el.style.marginRight === "auto"
              ? "center"
              : el.style.marginLeft === "auto"
                ? "right"
                : el.style.width === "100%"
                  ? "full"
                  : "left",
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const {
      styleName,
      backgroundColor,
      borderColor,
      fontColor,
      borderWidth,
      borderStyle,
      borderRadius,
      padding,
      shadow,
      textAlign,
      overflow,
      align,
    } = HTMLAttributes;

    const alignStyles =
      align === "center"
        ? "margin-left:auto;margin-right:auto;"
        : align === "right"
          ? "margin-left:auto;"
          : align === "full"
            ? "width:100%;"
            : "";

    const style = `
      background-color:${backgroundColor ?? ""};
      border-color:${borderColor ?? ""};
      border-width:${borderWidth ?? 1}px;
      border-style:${borderStyle ?? "solid"};
      border-radius:${borderRadius ?? 6}px;
      padding:${padding ?? 12}px;
      box-shadow:${shadow ?? "none"};
      text-align:${textAlign ?? "left"};
      overflow:${overflow ?? "visible"};
      color:${fontColor ?? ""};
      ${alignStyles}
    `;

    return [
      "div",
      {
        class: `collapsible panel panel-${styleName}`,
        style,
      },
      0,
    ];
  },
});

export const CollapsibleHeader = Node.create({
  name: "collapsibleHeader",
  group: "block",
  content: "collapsibleHeaderTitle",
  defining: true,

  parseHTML() {
    return [{
      tag: "div.collapsible-header",
      getContent: (element, schema) => {
        // The arrow is decoration, not document content. Parse only the title,
        // including older HTML with two nested title spans.
        const title = (element as HTMLElement).querySelector('.collapsible-header-title');
        const titleType = schema.nodes.collapsibleHeaderTitle;
        if (!title) return Fragment.from(titleType.create());
        const parser = ProseMirrorDOMParser.fromSchema(schema);
        const titleNode = new ProseMirrorDOMParser(schema, [
          { tag: 'span.collapsible-header-title', skip: true }, ...parser.rules,
        ]).parse(title, {
          topNode: titleType.create(),
        });
        return Fragment.from(titleNode);
      },
    }]
  },

  renderHTML() {
    return [
      "div",
      { class: "collapsible-header" },

      ["span", { class: "collapsible-icon", contenteditable: "false" },
        ["svg", { width: 14, height: 14, viewBox: "0 0 24 24" },
          ["path", { d: "M6 9l6 6 6-6" }]
        ]
      ],

      ["span", { class: "collapsible-header-content" }, 0]
    ]
  },
})

export const CollapsibleHeaderTitle = Node.create({
  name: "collapsibleHeaderTitle",
  group: "inline",
  inline: true,
  content: "text*",

  parseHTML() {
    return [{ tag: "span.collapsible-header-title" }]
  },

  renderHTML() {
    return ["span", { class: "collapsible-header-title" }, 0]
  },
})

export const CollapsibleBody = Node.create({
  name: "collapsibleBody",
  group: "block",
  content: "block+",
  defining: true,
  // Keep the default Enter handler from lifting an empty paragraph out of the
  // body. Splitting paragraphs and exiting a list still work inside this boundary.
  isolating: true,

  parseHTML() {
    return [{ tag: "div.collapsible-body" }]
  },

  renderHTML() {
    return ["div", { class: "collapsible-body" }, 0]
  },
})

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    editorPanel: {
      wrapInEditorPanel: (attrs?: Record<string, any>) => ReturnType;
      unwrapEditorPanel: () => ReturnType;
      setEditorPanelAttributes: (attrs: Record<string, any>) => ReturnType;
    };
  }
}

export const EditorPanel = Node.create({
  name: "editorPanel",

  group: "block",
  content: "block+",
  //defining: true,
  defining: false,
  isolating: true,

  selectable: true, 
  allowGapCursor: true,

  addAttributes() {
    return {
      styleName: { default: "default" },
      backgroundColor: { default: null },
      borderColor: { default: null },
      borderWidth: { default: 1 },
      borderStyle: { default: "solid" }, // NEW
      borderRadius: { default: 6 },
      padding: { default: 12 }, // numeric, not "12px"
      shadow: { default: "none" }, // NEW: preset
      align: { default: "left" }, // NEW: left | center | right | full
      textAlign: { default: null },
      overflow: { default: null },
      maxWidth: { default: null },
      fontColor: { default: null },
    };
  },

  parseHTML() {
    return [
        {
        tag: "div",
        getAttrs: (el) => {
            if (el.getAttribute("data-type") !== "editor-panel") return false;

            const style = el.getAttribute("style") ?? "";

            const get = (prop: string) => {
                const m = style.match(new RegExp(`${prop}:\\s*([^;]+)`));
                return m ? m[1].trim() : null;
            };

            function getStyleValue(style: string, prop: string): string | null {
                return style
                    .split(";")
                    .map(s => s.trim())
                    .filter(Boolean)
                    .reduce((acc, rule) => {
                    const [key, value] = rule.split(":").map(x => x.trim());
                    return key === prop ? value : acc;
                    }, null as string | null);
            }

            const classes = Array.from((el as HTMLElement).classList);
            const panelClass = classes.find(c => c.startsWith("panel-"));
            const styleName = panelClass ? panelClass.replace("panel-", "") : "default";

            return {
                styleName,
                backgroundColor: getStyleValue(style, "background-color"),
                borderColor: getStyleValue(style, "border-color"),
                fontColor: getStyleValue(style, "color"),
                borderWidth: parseInt(get("border-width") || "1"),
                borderStyle: get("border-style"),
                borderRadius: parseInt(get("border-radius") || "6"),
                padding: parseInt(get("padding") || "12"),
                shadow: get("box-shadow"),
                textAlign: get("text-align"),
                overflow: get("overflow"),
                align: el.style.marginLeft === "auto" && el.style.marginRight === "auto"
                    ? "center"
                    : el.style.marginLeft === "auto"
                    ? "right"
                    : el.style.width === "100%"
                        ? "full"
                        : "left",
                };
            },
        },
    ];
    },

  renderHTML({ HTMLAttributes }) {
    const {
      styleName,
      backgroundColor,
      borderColor,
      borderWidth,
      borderStyle,
      borderRadius,
      padding,
      shadow,
      align,
      textAlign,
      overflow,
      maxWidth,
      fontColor
    } = HTMLAttributes;

    //
    // Shadow presets (industry standard)
    //
    const shadowMap: Record<string, string> = {
      none: "none",
      sm: "0 1px 2px rgba(0,0,0,0.1)",
      md: "0 4px 6px rgba(0,0,0,0.1)",
      lg: "0 10px 15px rgba(0,0,0,0.15)",
    };

    //
    // Alignment behavior (industry standard)
    //
    let alignStyles = "";
    if (align === "center") {
      alignStyles = "margin-left: auto; margin-right: auto;";
    } else if (align === "right") {
      alignStyles = "margin-left: auto;";
    } else if (align === "full") {
      alignStyles = "width: 100%;";
    }

    const style = `
      ${backgroundColor ? `background-color: ${backgroundColor};` : ""}
      ${borderColor ? `border-color: ${borderColor};` : ""}
      ${borderWidth ? `border-width: ${borderWidth}px;` : ""}
      ${borderStyle ? `border-style: ${borderStyle};` : ""}
      ${borderRadius ? `border-radius: ${borderRadius}px;` : ""}
      ${padding !== null ? `padding: ${padding}px;` : ""}
      ${textAlign ? `text-align: ${textAlign};` : ""}
      ${overflow ? `overflow: ${overflow};` : ""}
      ${shadow ? `box-shadow: ${shadowMap[shadow] || "none"};` : ""}
      ${maxWidth ? `max-width: ${maxWidth};` : ""}
      ${fontColor ? `color: ${fontColor};` : ""}
      ${alignStyles}
    `;

    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "editor-panel",
        class: `panel panel-${styleName}`,
        style,
      }),
      0,
    ];
  },

  addKeyboardShortcuts() {
    return { Backspace: () => deletePreviousPanel(this.editor, this.name) };
  },

  addCommands() {
    return {
      wrapInEditorPanel:
        (attrs = {}) =>
        ({ chain }) =>
          chain().wrapIn(this.name, attrs).run(),

      unwrapEditorPanel:
        () =>
        ({ chain }) =>
          chain().lift(this.name).run(),

      setEditorPanelAttributes:
        (attrs) =>
        ({ commands }) =>
          commands.updateAttributes("editorPanel", attrs),
    };
  },
});

export interface ImageAttrs {
  src: string | null;
  alt: string | null;
  title: string | null;

  width: number | null;
  height: number | null;

  align: "left" | "center" | "right";

  caption: string | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    image: {
      setImage: (attrs: Partial<ImageAttrs>) => ReturnType;
    };
  }
}

export const ImageNode = Node.create({
  name: "image",

  group: "block",
  draggable: true,
  selectable: true,
  atom: true,

  parseHTML() {
    return [{
      tag: 'figure.image-block',
      getAttrs: element => element.querySelector('img[src]') ? null : false,
    }];
  },

  addAttributes() {
  return {
    // A temporary identity for asynchronous paste imports; never stored in HTML.
    importId: { default: null, renderHTML: () => ({}) },
    responsive: {
      default: false,
      parseHTML: element => imageElement(element)?.getAttribute('data-responsive') === 'true',
      renderHTML: attributes => attributes.responsive
        ? { 'data-responsive': 'true', style: 'max-width:100%;height:auto' } : {},
    },
    src: {
      default: null,
      parseHTML: element => element.tagName.toLowerCase() === 'svg'
        ? svgImageSource(element) : imageElement(element)?.getAttribute('src') || null,
    },
    alt: {
      default: null,
      parseHTML: element => imageElement(element)?.getAttribute('alt') || element.getAttribute('aria-label') || null,
    },
    title: {
      default: null,
      parseHTML: element => imageElement(element)?.getAttribute('title') || element.querySelector('title')?.textContent || null,
    },
    width: {
      default: null,
      parseHTML: element => imageDimension(element, 'width'),
    },
    height: {
      default: null,
      parseHTML: element => imageDimension(element, 'height'),
    },
    align: {
        default: 'center',
        parseHTML: (element) => {
            const cls = element.getAttribute('class') || '';
            if (cls.includes('align-left')) return 'left';
            if (cls.includes('align-right')) return 'right';
            return 'center';
        },
        },
    caption: {
      default: '',
      parseHTML: element => {
        const fig = element.querySelector('figcaption');
        return fig ? (fig.textContent || '').trim() : (imageElement(element)?.getAttribute('data-caption') || '');
      },
    },
  };
},

renderHTML({ HTMLAttributes }) {
  const alignment = HTMLAttributes.align || 'center';
  const figureClasses = `image-block align-${alignment}`;

  return [
    'figure',
    mergeAttributes(this.options.HTMLAttributes, { class: figureClasses },
      HTMLAttributes['data-responsive'] === 'true' ? {
        'data-responsive': 'true',
        style: 'width:100%;max-width:100%;min-width:0;box-sizing:border-box;margin-left:0;margin-right:0;overflow-wrap:anywhere',
      } : {}),
    [
      'img', 
      { 
        src: HTMLAttributes.src,
        alt: HTMLAttributes.alt,
        title: HTMLAttributes.title,
        width: HTMLAttributes.width,
        height: HTMLAttributes.height,
        ...(HTMLAttributes['data-responsive'] === 'true'
          ? { 'data-responsive': 'true', style: 'max-width:100%;height:auto' } : {}),
      }
    ],
    ['figcaption', { class: 'image-caption' }, HTMLAttributes.caption || ''],
  ];
},

  addOptions() {
    return {
      iframeDocument: null as Document | null,
    };
  },

  addCommands() {
    return {
        setImage: (attrs: Partial<any>) => ({ commands }) => {
        // Ensure we have a src before trying to insert, 
        // otherwise it returns false to tell Tiptap the command failed.
        if (!attrs.src) {
            return false;
        }

        return commands.insertContent({
            type: this.name,
            attrs,
        });
        },
    };
  },

  addNodeView() {
    return (props) => {
      const doc = this.options.iframeDocument || document;
      const dom = doc.createElement("figure");
      return new ImageView(props, doc, dom);
    };
  },
});

// Plain images in HTML belong to the surrounding paragraph/list/table cell.
// Explicit figures and standalone clipboard files continue to use ImageNode.
export const InlineImageNode = ImageNode.extend({
  name: 'inlineImage',
  group: 'inline',
  inline: true,
  parseHTML() { return [{ tag: 'img[src]' }, { tag: 'svg' }]; },
  addAttributes() {
    return Object.fromEntries(Object.entries(this.parent!())
      .filter(([name]) => name !== 'align' && name !== 'caption'));
  },
  addCommands() { return {}; },
  renderHTML({ HTMLAttributes }) { return ['img', HTMLAttributes]; },
  addNodeView() { return props => new InlineImageView(props); },
});

export type VideoAttrs = {
  url: string;
  provider: "youtube";
  videoId: string;
  align?: "left" | "center" | "right";
  width?: VideoWidth;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    video: {
      setVideo: (attrs: VideoAttrs) => ReturnType;
      setVideoAlign: (align: "left" | "center" | "right") => ReturnType;
    };
  }
}

export const VideoNode = Node.create({
  name: "video",

  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      url: { default: "" },
      provider: { default: "youtube" },
      videoId: { default: "" },
      width: {
        default: 640,
        parseHTML: element => videoWidth(element.getAttribute('data-video-width') ?? element.querySelector('iframe')?.getAttribute('width') ?? (element.querySelector('iframe') as HTMLIFrameElement | null)?.style.width),
        renderHTML: attributes => ({ 'data-video-width': videoWidth(attributes.width) }),
      },
      align: {
        default: "center",
        renderHTML: attributes => ({
            class: `align-${attributes.align || 'center'}`,
        }),
        parseHTML: element => {
            const cls = element.getAttribute('class') || '';
            if (cls.includes('align-left')) return 'left';
            if (cls.includes('align-right')) return 'right';
            return 'center';
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div.video-block",
        getAttrs: element => {
          const el = element as HTMLElement;
          const iframe = el.querySelector("iframe");
          if (!iframe) return false;

          const src = iframe.getAttribute("src") || "";
          const match = src.match(/youtube\.com\/embed\/([^?]+)/);
          if (!match) return false;

          // read align from class if present
          const className = el.className || "";
          let align: "left" | "center" | "right" = "center";
          if (className.includes("align-left")) align = "left";
          else if (className.includes("align-right")) align = "right";

          return {
            provider: "youtube",
            videoId: match[1],
            url: `https://www.youtube.com/watch?v=${match[1]}`,
            align,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { videoId } = HTMLAttributes as VideoAttrs;
    const align = HTMLAttributes.class?.includes('align-left') ? 'left' : HTMLAttributes.class?.includes('align-right') ? 'right' : 'center';

    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        class: `video-block align-${align}`,
        style: videoBlockStyle(align),
      }),
      [
        "iframe",
        {
          src: `https://www.youtube.com/embed/${videoId}`,
          style: videoFrameStyle(HTMLAttributes['data-video-width']),
          title: 'YouTube video',
          allow:
            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
          allowfullscreen: "true",
        },
      ],
    ];
  },

  addCommands() {
    return {
      setVideo:
        (attrs: VideoAttrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          });
        },

      setVideoAlign:
        (align: "left" | "center" | "right") =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, { align });
        },
    };
  },

  addNodeView() {
    return ({ node, editor, getPos, HTMLAttributes }) => {
        return new VideoView(node, editor, getPos, HTMLAttributes);
    };
  },
});

export type AudioAttrs = {
  src: string;
  align: "left" | "center" | "right";
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    audio: {
      setAudio: (attrs: AudioAttrs) => ReturnType;
      setAudioAlign: (align: "left" | "center" | "right") => ReturnType;
    };
  }
}

export const AudioNode = Node.create({
  name: "audio",

  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: "" },
      align: { default: "center" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div.audio-block",
        getAttrs: element => {
          const el = element as HTMLElement;
          const audio = el.querySelector("audio");
          if (!audio) return false;

          const src = audio.getAttribute("src") || "";

          let align: "left" | "center" | "right" = "center";
          const className = el.className || "";
          if (className.includes("align-left")) align = "left";
          else if (className.includes("align-right")) align = "right";

          return { src, align };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, align } = HTMLAttributes as AudioAttrs;

    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        class: `audio-block align-${align || 'center'}`,
      }),
      ["audio", { src, controls: "true" }],
    ];
  },

  addCommands() {
    return {
      setAudio:
        (attrs: AudioAttrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs,
          }),

      setAudioAlign:
        (align: "left" | "center" | "right") =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { align }),
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
        return new AudioView(node, editor, getPos);
    };
  },
});

export const HorizontalRule = Node.create({
  name: "horizontalRule",
  group: "block",
  atom: true,

  addAttributes() {
    return {
      variant: { default: "default" },
      spacing: { default: "md" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "hr",
        getAttrs: el => {
          const classList = (el as HTMLElement).classList;

          let variant = "default";
          let spacing = "md";

          for (let i = 0; i < classList.length; i++) {
            const c = classList.item(i);

            if (c && c.startsWith("hr-variant-")) {
              variant = c.replace("hr-variant-", "");
            }

            if (c && c.startsWith("hr-spacing-")) {
              spacing = c.replace("hr-spacing-", "");
            }
          }

          return { variant, spacing };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const { variant, spacing } = node.attrs;

    return [
      "hr",
      {
        class: `hr hr-variant-${variant} hr-spacing-${spacing}`,
      },
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const hr = document.createElement("hr");
      const { variant, spacing } = node.attrs;
      hr.className = `hr hr-variant-${variant} hr-spacing-${spacing}`;
      return { dom: hr };
    };
  },

  addCommands() {
    return {
      insertHR:
        (variant = "default", spacing = "md") =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { variant, spacing },
          }),

      setHRVariant:
        variant =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { variant }),

      setHRSpacing:
        spacing =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { spacing }),
    };
  },
});

export const StyleAttributeExtension = Extension.create({
  name: 'styleAttribute',
  addGlobalAttributes() {
    return [
      {
        // Add all types you want to be able to style via the inspector
        types: ['paragraph', 'heading', 'table', 'tableCell', 'tableRow', 'bulletList', 'orderedList'],
        attributes: {
          style: {
            default: null,
            // This is crucial: it tells Tiptap how to read the style from existing HTML
            parseHTML: element => element.getAttribute('style'),
            // This tells Tiptap how to write it back to the DOM
            renderHTML: attributes => {
              if (!attributes.style) return {};
              return { style: attributes.style };
            },
          },
        },
      },
    ];
  },
});

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+', // This allows one or more block-level nodes (p, ul, etc.) inside
  defining: true,    // Keeps the callout if you paste content into it
  
  addAttributes() {
    return {
      type: {
        default: 'info', // info, warning, error, success
        parseHTML: element => element.getAttribute('data-type'),
        renderHTML: attributes => ({ 'data-type': attributes.type }),
      },
    }
  },

  parseHTML() {
    return [
        {
        tag: 'div.callout',
        getAttrs: (node) => {
            const el = node as HTMLElement;
            
            // Strategy 1: Pull from data-type attribute (Most reliable)
            const dataType = el.getAttribute('data-type');
            if (dataType) return { type: dataType };

            // Strategy 2: Fallback - find the class that matches 'callout-'
            const calloutClass = Array.from(el.classList).find(c => c.startsWith('callout-'));
            if (calloutClass) {
            return { type: calloutClass.replace('callout-', '') };
            }

            return { type: 'info' }; // Ultimate fallback
        },
        },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'div', 
      mergeAttributes(HTMLAttributes, { class: `callout callout-${node.attrs.type}` }), 
      0 // The '0' tells Tiptap where the inner content (the paragraphs) should go
    ]
  },
});

export const TrailingNode = Extension.create({
  name: 'trailingNode',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('trailingNode'),
        appendTransaction: (transactions, oldState, newState) => {
          const { doc, tr, schema } = newState;
          const lastNode = doc.lastChild;
          const type = schema.nodes.paragraph;

          // If the last node is NOT a paragraph, add one.
          if (lastNode && lastNode.type !== type) {
            return tr.insert(doc.content.size, type.create());
          }
          return null;
        },
      }),
    ];
  },
});
